import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ACCESS_RESOURCE_TYPES } from '@infras/shared-types';
import type { RequestQuestion } from '../lib/access';

/**
 * Question templates for the access-request form (Settings → Request questions,
 * root+maintainer). A question can apply to every resource type or be scoped to
 * one. Deactivating hides it from new requests; existing answers keep their
 * prompt snapshot either way.
 */
const QUESTIONS = gql`
  query AdminRequestQuestions {
    requestQuestions(includeInactive: true) {
      id
      prompt
      helpText
      resourceType
      required
      sortOrder
      active
    }
  }
`;
const CREATE = gql`
  mutation CreateRequestQuestion($prompt: String!, $helpText: String, $resourceType: String, $required: Boolean, $sortOrder: Int) {
    createRequestQuestion(prompt: $prompt, helpText: $helpText, resourceType: $resourceType, required: $required, sortOrder: $sortOrder) {
      id
    }
  }
`;
const UPDATE = gql`
  mutation UpdateRequestQuestion($id: Int!, $required: Boolean, $active: Boolean, $resourceType: String) {
    updateRequestQuestion(id: $id, required: $required, active: $active, resourceType: $resourceType) {
      id
    }
  }
`;
const REMOVE = gql`
  mutation DeleteRequestQuestion($id: Int!) {
    deleteRequestQuestion(id: $id)
  }
`;

const ANY = '__any__';

export function RequestQuestions() {
  const { data, loading, error, refetch } = useQuery<{ requestQuestions: RequestQuestion[] }>(
    QUESTIONS,
    { fetchPolicy: 'cache-and-network' },
  );
  const [prompt, setPrompt] = useState('');
  const [help, setHelp] = useState('');
  const [scope, setScope] = useState(ANY);
  const [required, setRequired] = useState(true);
  const [mutErr, setMutErr] = useState<string | null>(null);

  const onDone = () => {
    setMutErr(null);
    void refetch();
  };
  const onErr = (e: Error) => setMutErr(e.message);
  const [create, { loading: creating }] = useMutation(CREATE, { onCompleted: onDone, onError: onErr });
  const [update, { loading: updating }] = useMutation(UPDATE, { onCompleted: onDone, onError: onErr });
  const [remove, { loading: removing }] = useMutation(REMOVE, { onCompleted: onDone, onError: onErr });

  const rows = data?.requestQuestions ?? [];
  const busy = creating || updating || removing;

  if (error) return <p className="error">{error.message}</p>;

  return (
    <div>
      <p className="muted text-xs">
        Questions the requester must answer on the new-request form. Scope a question to one
        resource type or leave it on <em>every type</em>. Answers snapshot the prompt — editing or
        deleting a template never rewrites submitted requests.
      </p>
      {mutErr && <p className="error">{mutErr}</p>}

      <div className="filter-row" style={{ alignItems: 'center' }}>
        <input
          placeholder="Question prompt, e.g. What level of access do you need?"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={{ minWidth: 320 }}
        />
        <input
          placeholder="help text (optional)"
          value={help}
          onChange={(e) => setHelp(e.target.value)}
          style={{ minWidth: 180 }}
        />
        <select className="class-select" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value={ANY}>every type</option>
          {ACCESS_RESOURCE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          required
        </label>
        <button
          className="btn btn-primary"
          disabled={busy || !prompt.trim()}
          onClick={() => {
            void create({
              variables: {
                prompt: prompt.trim(),
                helpText: help.trim() || null,
                resourceType: scope === ANY ? null : scope,
                required,
                sortOrder: rows.length,
              },
            });
            setPrompt('');
            setHelp('');
          }}
        >
          Add question
        </button>
      </div>

      {loading && !rows.length ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No questions yet — requests only ask for the free-form reason.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Prompt</th>
              <th>Scope</th>
              <th>Required</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.id} style={q.active ? undefined : { opacity: 0.55 }}>
                <td>
                  {q.prompt}
                  {q.helpText && (
                    <div className="muted text-2xs">
                      {q.helpText}
                    </div>
                  )}
                </td>
                <td>
                  <select
                    className="class-select class-select--sm"
                    value={q.resourceType ?? ANY}
                    disabled={busy}
                    onChange={(e) =>
                      void update({
                        variables: { id: Number(q.id), resourceType: e.target.value === ANY ? '' : e.target.value },
                      })
                    }
                  >
                    <option value={ANY}>every type</option>
                    {ACCESS_RESOURCE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={q.required}
                    disabled={busy}
                    onChange={(e) => void update({ variables: { id: Number(q.id), required: e.target.checked } })}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={q.active}
                    disabled={busy}
                    onChange={(e) => void update({ variables: { id: Number(q.id), active: e.target.checked } })}
                  />
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm('Delete this question? Submitted answers keep their snapshot.'))
                        void remove({ variables: { id: Number(q.id) } });
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
